import { Api } from "@/api/Api";
import { networkCaches } from "@/cache/GlobalCache";
import type {
  EnergyForecastAvailability,
  EnergyForecastTimeSeries,
  EnergyForecastType,
} from "@mm/energy.metx.meteomatics.com";

export const ENERGY_FORECAST_CACHE_STALE_TIME = 10 * 60 * 1000;

export type EnergyForecastRequest = {
  forecastType: EnergyForecastType;
  siteId: string;
  model: string;
};

export type EnergyForecastAvailabilityByType = Record<string, Record<string, EnergyForecastAvailability>>;

export function energyForecastCacheKey(request: EnergyForecastRequest) {
  return ["energy-forecast", "latest", request.forecastType, request.siteId, request.model];
}

export function energyForecastPortfolioAvailabilityCacheKey() {
  return ["energy-forecast", "portfolio-availability"];
}

/**
 * Shares latest Energy Forecast responses between map POIs and Portfolio Plot
 * through the global React Query cache. An aborted consumer does not cancel a
 * shared in-flight request that another consumer may still need.
 */
export async function requestCachedEnergyForecast(request: EnergyForecastRequest, signal?: AbortSignal) {
  signal?.throwIfAborted();
  const response = await networkCaches.energyForecast_cache.retrieve<
    EnergyForecastTimeSeries,
    Error,
    EnergyForecastTimeSeries
  >(energyForecastCacheKey(request), () => Api.energyForecast.getEnergyForecast(request), {
    staleTime: ENERGY_FORECAST_CACHE_STALE_TIME,
  });
  signal?.throwIfAborted();
  return response;
}

function requestPortfolioEnergyForecastAvailability() {
  return Api.energyForecast.getPortfolioEnergyForecastAvailability() as Promise<EnergyForecastAvailabilityByType>;
}

/** Returns the Energy Forecast sites that can be requested by a portfolio. */
export async function requestCachedPortfolioEnergyForecastAvailability(signal?: AbortSignal) {
  signal?.throwIfAborted();
  const availability = await networkCaches.energyForecast_cache.retrieve<
    EnergyForecastAvailabilityByType,
    Error,
    EnergyForecastAvailabilityByType
  >(energyForecastPortfolioAvailabilityCacheKey(), () => requestPortfolioEnergyForecastAvailability(), {
    staleTime: ENERGY_FORECAST_CACHE_STALE_TIME,
  });
  signal?.throwIfAborted();
  return availability;
}

export function isPortfolioEnergyForecastSiteAvailable(
  availability: EnergyForecastAvailabilityByType,
  request: EnergyForecastRequest,
) {
  return (
    availability[request.forecastType]?.[request.model]?.sites.some(
      (site) => site.site_id === request.siteId || site.site_ids.includes(request.siteId),
    ) ?? false
  );
}
